import {Injectable} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {
  catchError,
  combineLatestWith,
  map,
  switchMap,
  take,
  tap,
  withLatestFrom
} from 'rxjs/operators';
import {EMPTY, from, of} from 'rxjs';
import {AppState, selectCurrentAnnotationState} from '@store/app.states';
import {AnnotationService} from '@app/components/projects/surveys/annotation/services/annotation.service';
import {
  AnnotationActionTypes,
  GetDataUrl,
  LoadDataBegin,
  LoadDataBeginWithUrl,
  LoadDataFailure,
  LoadDataSuccess,
  SaveWithUpload,
  setDatasToAnnotationDatasServiceSuccess,
} from '@app/components/projects/surveys/annotation/store/annotation.actions';
import {Store} from '@ngrx/store';
import {ToastrService} from 'ngx-toastr';
import {AnnotationDataService} from '@app/components/projects/surveys/annotation/services/annotation.data.service';
import {
  AutoSaveService
} from '@app/components/projects/surveys/annotation/_components/annotations-manager/previous-auto-save/auto-save.service';
import {
  SurveyBatchOpenService
} from '../../../../../../@shared/_referencesLists/surveys-list/survey-batch-open.service';
import {
  AnnotationsDataV2Service
} from '@app/components/projects/surveys/annotation/services/annotations-data-v2.service';
import {
  CategoriesManagerService
} from '@app/components/projects/surveys/annotation/services/categories-manager.service';
import {MetaSurveysInfosService} from '@app/services/metas/meta-surveys-infos.service';
import {LoadS3FileService} from '../../../../../../@shared/_generalTools/load-s3-file.service';
import {
  AnnotationsRealtimeService
} from '@app/components/projects/surveys/annotation/services/annotations-realtime.service';

@Injectable()
export class AnnotationEffects {

  private counter;
  private target;

  constructor(
    private actions: Actions,
    private annotationService: AnnotationService,
    private annotationsDataV2Service: AnnotationsDataV2Service,
    private categoriesManagerService: CategoriesManagerService,
    private store: Store<AppState>,
    private toastr: ToastrService,
    private dataService: AnnotationDataService,
    private autoSaveService: AutoSaveService,
    private actions$: Actions,
    private surveyBatchOpenService: SurveyBatchOpenService,
    private metaSurveysInfosService: MetaSurveysInfosService,
    private loadS3FileService: LoadS3FileService,
    private annoRealTimeService: AnnotationsRealtimeService
  ) {

  }

  loadDataBegin = createEffect(() => this.actions.pipe(
    ofType(AnnotationActionTypes.LoadDataBegin),
    map((action: LoadDataBegin) => action.payload),
    switchMap(payload => {
      this.annotationsDataV2Service.surveyId = `${payload.surveyId}`;
      this.annoRealTimeService.setupNewSurvey(`${payload.surveyId}`);
      this.metaSurveysInfosService
        .loadSurveysResults([`${payload.surveyId}`], false, [], false)
        .pipe(take(1))
        .subscribe((result) => {
          this.metaSurveysInfosService.loadResultsFromS3(result.data.datas.result.jsonUrl)
            .pipe(take(1))
            .subscribe((data) => {
              this.annotationsDataV2Service.setRawResults(data.rawResults.responses.filter((r) => r.is_valid));
            });
        });
      return this.annoRealTimeService.testForData().pipe(
        map((fbData) => {
          if (fbData){
            return new LoadDataSuccess({data: fbData, fromFB: true});
          }else {
            return new GetDataUrl({surveyId: payload.surveyId});
          }
        })
      );

    })
  ));

  GetDataUrl = createEffect(() => this.actions.pipe(
    ofType(AnnotationActionTypes.GetDataUrl),
    map((action: LoadDataBegin) => action.payload),
    switchMap(payload => {
      return this.annotationService.get(payload.surveyId)
        .pipe(
          map((data) => new LoadDataBeginWithUrl({url: data.data.jsonUrl})),
          catchError(
            error => {
              return of(new LoadDataFailure({error}));
            }
          )
        );
    })
  ));


  loadDataBeginWithUrl = createEffect(() => this.actions.pipe(
    ofType(AnnotationActionTypes.LoadDataBeginWithUrl),
    map((action: LoadDataBeginWithUrl) => action.payload),
    switchMap(action => {

      return this.loadS3FileService.loadFileFromS3(action.url)
        .pipe(
          map(data => {
            return new LoadDataSuccess({data});
          }),
          catchError(error => {
              return of(new LoadDataFailure({error}));
            }
          )
        );
    })
  ));

  loadAnnotationsResultsSuccess$ = createEffect(() =>
    this.actions.pipe(
      ofType(AnnotationActionTypes.LoadDataSuccess),
      map((action: LoadDataSuccess) => action),
      withLatestFrom(this.store.select(selectCurrentAnnotationState)),
      map((state) => state),
      switchMap(([payload, state]) => {

        //
        // Cas pas de réponse annotable et batchMode...
        //
        const data = payload.payload.data;

        if (state.batchMode) {
          this.metaSurveysInfosService.getSurveysMeta$(data.surveyId || `${data.survey.info.id}`)
            .pipe(switchMap(survey => survey ? of(survey.surveyDef) : EMPTY), take(1))
            .subscribe(surveyDef => {
              const questions = surveyDef.questions;
              if (Object.values(questions).every((question) => {
                return !['open', 'props'].includes(question.type) &&
                  !question.rowsOrder.some(rKey => Object.values(question.rows[rKey].openCols).some(open => open));
              })) {
                setTimeout(() => {

                  this.toastr.error(
                    `Continue batch....`,
                    `[${data.surveyId || state?.survey?.info?.id}] No annotable question... :(`,
                    {
                      timeOut: 5000,
                      progressBar: true,
                      enableHtml: true
                    });
                  this.surveyBatchOpenService.signalIframeClosed();
                }, 500);
              }
            });
        }
        if (!payload.payload.fromFB) {
          if (payload.payload.data.version === 2024) {
            this.annotationsDataV2Service.setResponsesIndex2024(data.dictionaries, data.responses);
            this.categoriesManagerService.setCategories(data.categories, true);
          } else {
            this.annotationsDataV2Service.setResponsesIndexOld(data.responses, data.version);
            if (data.meta.currentManagerState?.groupTree) {
              this.categoriesManagerService.setCategories(data.meta.currentManagerState?.groupTree);
            }
          }
        }
        return this.dataService.obsSetDAnnotationsData(
        ).pipe(
          combineLatestWith(this.annotationsDataV2Service.annoAllSet$, this.categoriesManagerService.categoryAllSet$),
          switchMap((allSet) => allSet[1] && allSet[2] ? of(allSet) : EMPTY),
          map((result) => {
            if (state?.batchMode) {
              this.store.dispatch(new SaveWithUpload({surveyId: data.surveyId || `${state?.survey?.info?.id}`}));
            }
            return setDatasToAnnotationDatasServiceSuccess({nbRest: result[0]});
          })
        );
      })
    )
  );


  ReinitStore = createEffect(() => this.actions.pipe(
    ofType(AnnotationActionTypes.ReinitStore),
    tap((action) => {

      this.autoSaveService.clearService();


    })
  ), {dispatch: false});


}


